import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable, of, switchMap } from "rxjs";
import { map } from "rxjs/operators";
import { IPS_CUTOFF_AGE, IPS_MAX_YEARLY_SAVINGS_QUOTA } from "src/app/constants/business.constants";
import { ContractTypeOf } from "src/app/models/engagements/generic-engagement.model";
import { IpsEngagement } from "src/app/models/engagements/savings-and-pension/link-engagement.model";
import { AddLumpSumActionBuilder } from "src/app/modules/shared/services/contract-actions/action-builders/add-lump-sum-action-builder";
import { ChangeMonthlySavingsActionBuilder } from "src/app/modules/shared/services/contract-actions/action-builders/change-monthly-savings-action-builder";
import { ContractDetailsService } from "src/app/modules/shared/services/contract-details.service";
import * as Graph from "src/app/services/api/savings-and-pension-queries.types";
import { CustomerService } from "src/app/services/customer.service";
import {
  AnySavingsAndPensionEngagement,
  SavingsAndPensionService,
} from "src/app/services/prognoses-services/savings-and-pension.service";
import { isIpsEngagement } from "src/app/utils/engagement.typeguards";
import { getIsNullable } from "src/app/utils/utils";
import { NavigateToService } from "src/app/services/navigate-to.service";

export type EngagementWithContractDetails = [IpsEngagement, ContractTypeOf<IpsEngagement>];

export enum IpsAction {
  StartIps = "startIps",
  IncreaseIps = "increaseIps",
  Unqualified = "unqualified",
}

@Injectable({
  providedIn: "root",
})
export class ActionIpsService {
  private readonly customerService = inject(CustomerService);
  private readonly savingsAndPensionService = inject(SavingsAndPensionService);
  private readonly contractDetailsService = inject(ContractDetailsService);
  private readonly addLumpSumActionBuilder = inject(AddLumpSumActionBuilder);
  private readonly changeMonthlySavingsActionBuilder = inject(ChangeMonthlySavingsActionBuilder);
  private readonly navigateToService = inject(NavigateToService);

  private readonly override$ = new BehaviorSubject<IpsAction | undefined>(undefined);

  public getFirstEngagement(): Observable<IpsEngagement | undefined> {
    return this.getEngagements().pipe(map((engagements) => engagements?.[0]));
  }

  public getIpsAction(): Observable<IpsAction> {
    return combineLatest([this.customerService.age$, this.getEngagements(), this.override$]).pipe(
      switchMap(([age, engagements, override]) => {
        if (override) {
          return of(override);
        }

        if (!age || age >= IPS_CUTOFF_AGE || engagements.length >= 2) {
          return of(IpsAction.Unqualified);
        }

        const firstEngagment = engagements[0];

        if (engagements.length === 0 || getIsNullable(firstEngagment)) {
          return of(IpsAction.StartIps);
        }

        return this.getCanIncreaseDeposit(firstEngagment).pipe(
          map((canIncreaseDeposit) => (canIncreaseDeposit ? IpsAction.IncreaseIps : IpsAction.Unqualified)),
        );
      }),
    );
  }

  public overrideIps(state: IpsAction | undefined): void {
    this.override$.next(state);
  }

  public getDepositThisYear(engagement: IpsEngagement): Observable<number> {
    return this.getFirstEngagementWithContractDetails(engagement).pipe(
      map(([, contractDetails]) => {
        const savingsAgreements = engagement.getActiveSavingsAgreements();
        return savingsAgreements.length === 0
          ? getTotalDepositLastQueryYear(contractDetails)
          : getYearlyDeposits(savingsAgreements);
      }),
    );
  }

  public async openLumpSumApp(engagement: IpsEngagement): Promise<void> {
    this.navigateToService.navigateToExternalUrl(this.addLumpSumActionBuilder.build(engagement).linkUrl);
  }

  public async openMonthlySavingsApp(engagement: IpsEngagement): Promise<void> {
    this.navigateToService.navigateToExternalUrl(this.changeMonthlySavingsActionBuilder.build(engagement).linkUrl);
  }

  public isIpsQualified(): Observable<boolean> {
    return this.getIpsAction().pipe(map((action) => action === IpsAction.StartIps));
  }

  private getCanIncreaseDeposit(engagement: IpsEngagement): Observable<boolean> {
    return this.getDepositThisYear(engagement).pipe(map((deposit) => deposit < IPS_MAX_YEARLY_SAVINGS_QUOTA));
  }

  private getEngagements(): Observable<IpsEngagement[]> {
    return this.savingsAndPensionService.engagements$.pipe(map(getIpsEngagements));
  }

  private getFirstEngagementWithContractDetails(engagement: IpsEngagement): Observable<EngagementWithContractDetails> {
    return this.contractDetailsService
      .fetchContractDetails(engagement)
      .pipe(
        map<ContractTypeOf<IpsEngagement>, EngagementWithContractDetails>((contractDetails) => [
          engagement,
          contractDetails,
        ]),
      );
  }
}

function getIpsEngagements(engagements: AnySavingsAndPensionEngagement[]): IpsEngagement[] {
  return engagements.filter(isIpsEngagement);
}

function getYearlyDeposits(savingsAgreements: Graph.SavingsAgreement[]): number {
  return savingsAgreements.reduce((sum, { yearlyAmount }) => sum + (yearlyAmount ?? 0), 0);
}

function getTotalDepositLastQueryYear(contract: ContractTypeOf<IpsEngagement>): number {
  return contract?.keyValues?.totalDepositLastQueryYear?.value ?? 0;
}
